import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
    PlusIcon,
    Trash2Icon,
    CreditCardIcon,
    Loader2Icon,
    AlertCircleIcon,
    CheckCircle2Icon,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Switch } from "@component/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@component/sheet";
import {
    createPackage,
    updatePackage,
    syncPackageToStripe,
    ADMIN_PACKAGE_PLATFORMS,
    AdminPackageError,
} from "@service/admin-subscription-packages";
import type {
    AdminSubscriptionPackage,
    AdminPackagePlatform,
    AdminPackagePayload,
    AdminPackageDetails,
    AdminPackageCurrencyDetail,
} from "@service/admin-subscription-packages";
import { cn } from "@util/tailwind";
import { parseFormat } from "@util/date";

const PLATFORM_LABEL: Record<AdminPackagePlatform, string> = {
    recruiter: "Recruiter",
    training: "Training",
    agent: "Agent",
};

// Feature keys the form seeds per PLATFORM when creating/editing a package
// (#2726). Each platform carries only its OWN keys — training/agent packages must
// NOT inherit recruiter keys. The BE strips mismatched keys defensively
// (ourteam-backend#3256), but the form should send the right ones so the admin's
// values aren't silently dropped. Admins can still add/remove rows freely — the BE
// `features` map is package-specific, not a fixed schema.
//
// Agent keys are intentionally empty: the ticket/handoff doesn't enumerate them,
// so we seed none (the admin adds agent-specific rows manually) rather than invent
// keys — what matters is that an agent package carries no recruiter/training keys.
const PLATFORM_FEATURE_KEYS: Record<AdminPackagePlatform, string[]> = {
    recruiter: ["cv_screening", "ai_assessment", "limit_users", "full_ats_workflow"],
    training: ["limit_campaign", "limit_session", "limit_audio_upload", "limit_document_upload"],
    agent: [],
};

// All currencies an admin can price (BE contract). SGD/MYR are Stripe-syncable
// (separate Stripe accounts, BE #3159); IDR is stored/displayed only.
const DETAIL_CURRENCIES = ["SGD", "MYR", "IDR"] as const;
type DetailCurrency = (typeof DETAIL_CURRENCIES)[number];

// The subset Stripe sync supports (SGD → SG account, MYR → MY account, #3212).
const SYNCABLE_CURRENCIES: DetailCurrency[] = ["SGD", "MYR"];

// A per-currency pricing field. `price` is the admin-set TOTAL package price (not
// derived from price_month); discount/tax are percentages; the rest are major-unit
// amounts. The BE stores a missing field as 0.
interface DetailField {
    key: string;
    label: string;
    suffix?: string;
}

// Base fields every currency block carries, regardless of platform.
const BASE_DETAIL_FIELDS: DetailField[] = [
    { key: "price_month", label: "Monthly price" },
    { key: "price", label: "Total price" },
    { key: "discount", label: "Discount", suffix: "%" },
    { key: "tax", label: "Tax", suffix: "%" },
];

// Platform-specific add-on price fields (the per-unit features that carry an
// extra-unit price). The `details` block is platform-dependent just like
// `features` — recruiter has its CV/AI add-on prices; training/agent are base-only
// until their BE contract defines add-on fields (then add the keys here).
const PLATFORM_DETAIL_ADDONS: Record<AdminPackagePlatform, DetailField[]> = {
    recruiter: [
        { key: "cv_screening_extra_price", label: "CV screening add-on" },
        { key: "ai_assessment_extra_price", label: "AI screening add-on" },
    ],
    training: [],
    agent: [],
};

// The fields rendered/sent for a platform = base + that platform's add-ons.
function detailFieldsFor(platform: AdminPackagePlatform): DetailField[] {
    return [...BASE_DETAIL_FIELDS, ...PLATFORM_DETAIL_ADDONS[platform]];
}

// Every field key any platform can have — used to seed/read form state so a
// value survives a platform switch (only the RENDERED subset changes).
const ALL_DETAIL_FIELD_KEYS = Array.from(
    new Set([
        ...BASE_DETAIL_FIELDS.map((f) => f.key),
        ...Object.values(PLATFORM_DETAIL_ADDONS)
            .flat()
            .map((f) => f.key),
    ])
);

// Per-currency editable form state: each field kept as a string and coerced to a
// number on submit (blank → 0, matching the BE "missing → 0" normalisation).
type CurrencyDetailForm = Record<string, string>;
type DetailForm = Record<DetailCurrency, CurrencyDetailForm>;

function emptyCurrencyDetail(): CurrencyDetailForm {
    return ALL_DETAIL_FIELD_KEYS.reduce((acc, key) => {
        acc[key] = "";
        return acc;
    }, {} as CurrencyDetailForm);
}

// A single editable feature row. `value` is kept as a string in form state and
// coerced to boolean/number/string on submit so the BE map stays typed.
interface FeatureRow {
    key: string;
    value: string;
}

// Coerce a feature row's string value into the natural JSON type:
// "true"/"false" → boolean, a clean numeric string → number, else string.
function coerceFeatureValue(value: string): boolean | number | string {
    const trimmed = value.trim();
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    if (trimmed !== "" && !Number.isNaN(Number(trimmed))) return Number(trimmed);
    return value;
}

// Render a stored feature value back into the editable string field.
function featureValueToString(value: unknown): string {
    if (typeof value === "boolean") return value ? "true" : "false";
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function humanizeKey(key: string): string {
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Every key that is "owned" by some platform — used to tell a wrong-platform key
// (drop it) apart from a truly custom key (keep it).
const ALL_PLATFORM_FEATURE_KEYS = new Set(
    Object.values(PLATFORM_FEATURE_KEYS).flat()
);

// Build the feature rows for a platform: keep the package's existing
// selected-platform keys + any truly custom (unknown) keys, DROP keys that are
// known to belong to a DIFFERENT platform (so editing an older mis-keyed package
// doesn't re-render/resubmit e.g. recruiter keys on a training package, #2726),
// then offer that platform's known keys not already present.
function buildFeatureRows(
    features: Record<string, unknown> | undefined,
    platform: AdminPackagePlatform
): FeatureRow[] {
    const platformKeys = PLATFORM_FEATURE_KEYS[platform] ?? [];
    const rows: FeatureRow[] = [];
    const seen = new Set<string>();
    for (const [key, value] of Object.entries(features ?? {})) {
        // Drop only keys known to belong to another platform; keep this
        // platform's keys and unknown custom keys.
        if (ALL_PLATFORM_FEATURE_KEYS.has(key) && !platformKeys.includes(key)) {
            continue;
        }
        rows.push({ key, value: featureValueToString(value) });
        seen.add(key);
    }
    for (const key of platformKeys) {
        if (!seen.has(key)) rows.push({ key, value: "" });
    }
    return rows.length > 0 ? rows : platformKeys.map((key) => ({ key, value: "" }));
}

// Collapse the current editable rows back into a `{ key: value }` object so a
// platform switch can re-run buildFeatureRows() WITHOUT losing the admin's filled
// values / custom rows — only wrong-platform keys get dropped (#2726).
function featureRowsToObject(rows: FeatureRow[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const row of rows) {
        const key = row.key.trim();
        if (!key) continue;
        out[key] = row.value;
    }
    return out;
}

// The stored object for one currency, e.g. `details.SGD = { price_month, … }`.
// Returns null if `details[ccy]` isn't a usable object.
function currencyEntry(
    details: Record<string, unknown> | undefined,
    ccy: string
): Record<string, unknown> | null {
    const entry = details?.[ccy];
    if (entry != null && typeof entry === "object" && !Array.isArray(entry)) {
        return entry as Record<string, unknown>;
    }
    return null;
}

// Populate the per-currency editable form from the package's stored `details`
// (per-currency shape `details.<CCY> = { price_month, price, … }`). A 0 — the
// BE's stored value for a "missing" field — is shown blank to keep the form
// readable, and round-trips back to 0 on save.
//
// Backward-compat: a package still stored in the LEGACY FLAT shape
// (`{ price_month, price, currency }` at the top level, no per-currency keys)
// is hydrated onto the currency named by `details.currency` (default SGD) so an
// un-migrated package keeps its price on edit instead of loading blank and
// getting zeroed on the next save. It migrates to the per-currency shape on save.
function readDetailForm(
    details: Record<string, unknown> | undefined
): DetailForm {
    const d = details ?? {};
    const out = {} as DetailForm;
    const flatCurrency =
        typeof d.currency === "string" && d.currency.trim()
            ? d.currency.trim().toUpperCase()
            : "SGD";
    for (const ccy of DETAIL_CURRENCIES) {
        const entry = currencyEntry(d, ccy);
        const row = emptyCurrencyDetail();
        if (entry) {
            for (const key of ALL_DETAIL_FIELD_KEYS) {
                const v = entry[key];
                if (typeof v === "number" && Number.isFinite(v) && v !== 0) {
                    row[key] = String(v);
                }
            }
        } else if (flatCurrency === ccy) {
            // Legacy flat shape → seed from the top-level fields.
            for (const key of ALL_DETAIL_FIELD_KEYS) {
                const v = d[key];
                if (typeof v === "number" && Number.isFinite(v) && v !== 0) {
                    row[key] = String(v);
                }
            }
        }
        out[ccy] = row;
    }
    return out;
}

interface AdminSubscriptionPackageFormProps {
    open: boolean;
    onClose: () => void;
    // Present = edit mode; absent = create mode.
    pkg?: AdminSubscriptionPackage | null;
    // Called after a successful create/edit/sync so the list can refresh.
    onSaved: () => void;
}

export default function AdminSubscriptionPackageForm({
    open,
    onClose,
    pkg,
    onSaved,
}: AdminSubscriptionPackageFormProps) {
    const isEdit = Boolean(pkg);

    const [packageName, setPackageName] = useState("");
    const [description, setDescription] = useState("");
    const [platform, setPlatform] = useState<AdminPackagePlatform>("recruiter");
    const [isActive, setIsActive] = useState(true);
    // Hidden from the public catalog (BE `is_shadow_package`, #3004). New
    // packages default to visible (false).
    const [isShadowPackage, setIsShadowPackage] = useState(false);
    // Billing duration in months (string in form state, coerced to a number on submit).
    const [duration, setDuration] = useState("");
    // Display sort order (BE `sort_order`); string in form state.
    const [sortOrder, setSortOrder] = useState("");
    const [featureRows, setFeatureRows] = useState<FeatureRow[]>([]);
    // Per-currency pricing detail (all 6 fields × SGD/MYR/IDR). Strings in form
    // state; coerced to numbers on submit.
    const [detailForm, setDetailForm] = useState<DetailForm>(() =>
        readDetailForm(undefined)
    );

    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Stripe-sync state. We keep the locally-synced package so the chips reflect
    // the latest sync result without needing a list refetch.
    const [syncing, setSyncing] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [syncedPkg, setSyncedPkg] =
        useState<AdminSubscriptionPackage | null>(null);

    // (Re)seed form state whenever the drawer opens or the target package
    // changes. Resets to create-defaults when `pkg` is absent.
    useEffect(() => {
        if (!open) return;
        setPackageName(pkg?.packageName ?? "");
        setDescription(pkg?.description ?? "");
        setPlatform(pkg?.platform ?? "recruiter");
        setIsActive(pkg?.isActive ?? true);
        setIsShadowPackage(pkg?.isShadowPackage ?? false);
        setDuration(pkg?.duration != null ? String(pkg.duration) : "");
        setSortOrder(pkg?.sortOrder != null ? String(pkg.sortOrder) : "");
        setFeatureRows(buildFeatureRows(pkg?.features, pkg?.platform ?? "recruiter"));
        setDetailForm(readDetailForm(pkg?.details));
        setErrors({});
        setSyncError(null);
        setSyncedPkg(pkg ?? null);
    }, [open, pkg]);

    // The package whose Stripe state we display — the freshest one available.
    const stripePkg = syncedPkg ?? pkg ?? null;
    const stripeProductIds = stripePkg?.stripeProductIds ?? null;
    const stripePriceIds = stripePkg?.stripePriceIds ?? null;
    const stripeSyncedAt = stripePkg?.syncedAt ?? null;
    const stripeSynced = useMemo(() => {
        if (!stripePkg) return false;
        return (
            Boolean(stripePkg.stripeProductId) ||
            (stripeProductIds && Object.keys(stripeProductIds).length > 0) ||
            (stripePriceIds && Object.keys(stripePriceIds).length > 0)
        );
    }, [stripePkg, stripeProductIds, stripePriceIds]);

    function updateFeatureRow(index: number, patch: Partial<FeatureRow>) {
        setFeatureRows((rows) =>
            rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
        );
    }

    function addFeatureRow() {
        setFeatureRows((rows) => [...rows, { key: "", value: "" }]);
    }

    function removeFeatureRow(index: number) {
        setFeatureRows((rows) => rows.filter((_, i) => i !== index));
    }

    function updateDetailField(
        ccy: DetailCurrency,
        field: string,
        value: string
    ) {
        setDetailForm((prev) => ({
            ...prev,
            [ccy]: { ...prev[ccy], [field]: value },
        }));
    }

    // Required-field validation. Returns true when the form is valid. `forSync`
    // additionally requires at least one syncable currency to carry a valid
    // price (Stripe needs a price to create) — plain saves don't.
    function validate(forSync = false): boolean {
        const next: Record<string, string> = {};
        if (!packageName.trim()) next.packageName = "Package name is required.";
        if (!platform) next.platform = "Platform is required.";

        // Duration (months): optional, but if filled it must be a whole number >= 0.
        // 0 is allowed — it marks a package with no fixed billing length (e.g. a
        // free Trial Workspace), rendered as "—" in the list.
        const durationRaw = duration.trim();
        if (durationRaw !== "") {
            const d = Number(durationRaw);
            if (!Number.isInteger(d) || d < 0) {
                next.duration = "Enter a whole number of months (0 or more).";
            }
        }

        // Sort order: optional, but if filled it must be a whole number >= 0.
        const sortRaw = sortOrder.trim();
        if (sortRaw !== "") {
            const s = Number(sortRaw);
            if (!Number.isInteger(s) || s < 0) {
                next.sortOrder = "Enter a whole number (0 or more).";
            }
        }

        // Per-currency detail validation: every filled field (for the selected
        // platform's field set) must be a valid non-negative number. Track
        // syncable currencies (SGD/MYR) that carry a usable (> 0) monthly price
        // for the Stripe-sync gate.
        const detailFields = detailFieldsFor(platform);
        let syncPriced = 0;
        for (const ccy of DETAIL_CURRENCIES) {
            for (const field of detailFields) {
                const raw = (detailForm[ccy][field.key] ?? "").trim();
                if (raw === "") continue;
                const n = Number(raw);
                if (Number.isNaN(n) || n < 0) {
                    next[`detail_${ccy}_${field.key}`] = "Enter 0 or more.";
                }
            }
            const pm = Number((detailForm[ccy].price_month ?? "").trim());
            if (SYNCABLE_CURRENCIES.includes(ccy) && Number.isFinite(pm) && pm > 0) {
                syncPriced += 1;
            }
        }
        if (forSync && syncPriced === 0) {
            next.price =
                "Set a monthly price for at least one Stripe currency (SGD or MYR) to sync.";
        }

        // Feature rows with a value but no key are invalid (orphan value).
        const orphan = featureRows.some(
            (r) => !r.key.trim() && r.value.trim() !== ""
        );
        if (orphan) next.features = "Every feature with a value needs a key.";
        setErrors(next);
        return Object.keys(next).length === 0;
    }

    // Assemble the camelCase payload from form state.
    function buildPayload(): AdminPackagePayload {
        const features: Record<string, unknown> = {};
        for (const row of featureRows) {
            const key = row.key.trim();
            if (!key) continue;
            features[key] = coerceFeatureValue(row.value);
        }

        // Per-currency `details` (BE contract):
        //   details = { SGD: { …6 fields… }, MYR: { … }, IDR: { … } }.
        // For each currency the admin set, send the COMPLETE 6-field block (a
        // blank field → 0) — the BE stores the full block, so a partial block
        // would reset the omitted fields to 0. A currency with NO values at all
        // is omitted (left untouched on the BE).
        const details: AdminPackageDetails = {};
        // Defensive: preserve any stored currency block NOT in our editor set.
        for (const [key, val] of Object.entries(pkg?.details ?? {})) {
            if (
                !(DETAIL_CURRENCIES as readonly string[]).includes(key) &&
                val != null &&
                typeof val === "object"
            ) {
                details[key] = val as AdminPackageCurrencyDetail;
            }
        }
        const detailFields = detailFieldsFor(platform);
        for (const ccy of DETAIL_CURRENCIES) {
            const row = detailForm[ccy];
            const hasAny = detailFields.some(
                (field) => (row[field.key] ?? "").trim() !== ""
            );
            if (!hasAny) continue;
            const block: AdminPackageCurrencyDetail = {};
            for (const field of detailFields) {
                const raw = (row[field.key] ?? "").trim();
                const n = raw === "" ? 0 : Number(raw);
                block[field.key] = Number.isFinite(n) ? n : 0;
            }
            details[ccy] = block;
        }

        const durationRaw = duration.trim();
        const sortRaw = sortOrder.trim();
        return {
            packageName: packageName.trim(),
            description: description.trim() ? description.trim() : null,
            platform,
            isActive,
            isShadowPackage,
            duration: durationRaw === "" ? null : Number(durationRaw),
            sortOrder: sortRaw === "" ? null : Number(sortRaw),
            features,
            details,
        };
    }

    async function handleSubmit() {
        if (submitting) return;
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload = buildPayload();
            if (isEdit && pkg) {
                await updatePackage(pkg.id, payload);
                toast.success("Package updated.");
            } else {
                await createPackage(payload);
                toast.success("Package created.");
            }
            onSaved();
            onClose();
        } catch (err) {
            const message =
                err instanceof AdminPackageError
                    ? err.message
                    : isEdit
                      ? "Could not update the package."
                      : "Could not create the package.";
            // 409 = duplicate package name (global uniqueness) — pin it to the
            // name field so the admin sees exactly what to change.
            if (err instanceof AdminPackageError && err.status === 409) {
                setErrors((prev) => ({ ...prev, packageName: message }));
            }
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSync() {
        if (!pkg || syncing) return;
        // Stripe needs a usable monthly price on at least one syncable currency;
        // validate (in sync mode) before the BE call.
        if (!validate(true)) return;
        setSyncing(true);
        setSyncError(null);
        try {
            // Persist the current form first so the BE has the per-currency
            // `details` (`{ SGD: { price_month }, MYR: { price_month } }`,
            // ourteam-backend#3212) the admin sees — THEN sync. The sync reads
            // `details.<CCY>.price_month` per currency and creates one Stripe
            // Price each. The drawer stays open to show the result.
            await updatePackage(pkg.id, buildPayload());
            const updated = await syncPackageToStripe(pkg.id);
            setSyncedPkg(updated);
            toast.success("Saved and synced to Stripe.");
            // The save + sync both mutated the package — let the list refresh.
            onSaved();
        } catch (err) {
            // Surface the documented failures with a clear, status-specific line
            // (400 no price / 409 billing disabled / 502 Stripe), falling back
            // to the BE message.
            const status =
                err instanceof AdminPackageError ? err.status : null;
            const beMessage =
                err instanceof AdminPackageError ? err.message : null;
            let message = beMessage ?? "Could not sync this package to Stripe.";
            if (status === 400) {
                message =
                    beMessage ??
                    "Set a valid monthly price for each currency you're syncing — Stripe needs a price to create.";
            } else if (status === 409) {
                message =
                    beMessage ?? "Stripe billing is disabled for this environment.";
            } else if (status === 502) {
                message =
                    beMessage ?? "Stripe rejected the request. Try again shortly.";
            }
            setSyncError(message);
            toast.error(message);
        } finally {
            setSyncing(false);
        }
    }

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full sm:max-w-xl overflow-y-auto flex flex-col gap-0 p-0">
                {/* Header */}
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-gray-200">
                    <SheetTitle className="text-lg font-semibold text-gray-900">
                        {isEdit ? "Edit package" : "New package"}
                    </SheetTitle>
                    <SheetDescription className="text-sm text-gray-500">
                        {isEdit
                            ? "Update this subscription package and its pricing."
                            : "Create a subscription package for a platform."}
                    </SheetDescription>
                </SheetHeader>

                {/* Body */}
                <div className="flex-1 px-6 py-6 space-y-6">
                    {/* Package name */}
                    <div className="space-y-2">
                        <Label htmlFor="package-name">
                            Package name
                            <span className="text-rose-500 ml-0.5">*</span>
                        </Label>
                        <Input
                            id="package-name"
                            placeholder="Growth"
                            value={packageName}
                            onChange={(e) => setPackageName(e.target.value)}
                            disabled={submitting}
                            aria-invalid={Boolean(errors.packageName)}
                            className={cn(
                                errors.packageName &&
                                    "border-rose-300 focus-visible:ring-rose-200"
                            )}
                        />
                        {errors.packageName && (
                            <p className="text-xs text-rose-600">
                                {errors.packageName}
                            </p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="package-description">Description</Label>
                        <Textarea
                            id="package-description"
                            placeholder="What this package includes…"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={submitting}
                            className="min-h-[80px] resize-none"
                        />
                    </div>

                    {/* Platform */}
                    <div className="space-y-2">
                        <Label htmlFor="package-platform">
                            Platform
                            <span className="text-rose-500 ml-0.5">*</span>
                        </Label>
                        <Select
                            value={platform}
                            onValueChange={(v) => {
                                const next = v as AdminPackagePlatform;
                                setPlatform(next);
                                // Remap the CURRENT rows to the new platform instead
                                // of wiping them: keep filled values + custom keys,
                                // drop only the previous platform's keys, add the new
                                // platform's keys (#2726).
                                setFeatureRows((current) =>
                                    buildFeatureRows(
                                        featureRowsToObject(current),
                                        next
                                    )
                                );
                            }}
                            disabled={submitting}
                        >
                            <SelectTrigger
                                id="package-platform"
                                aria-label="Platform"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ADMIN_PACKAGE_PLATFORMS.map((p) => (
                                    <SelectItem key={p} value={p}>
                                        {PLATFORM_LABEL[p]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Duration — billing length of the package, in months */}
                    <div className="space-y-2">
                        <Label htmlFor="package-duration">Duration (months)</Label>
                        <Input
                            id="package-duration"
                            inputMode="numeric"
                            placeholder="e.g. 1, 3, 12"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            disabled={submitting}
                            aria-invalid={Boolean(errors.duration)}
                            className={cn(
                                "tabular-nums",
                                errors.duration &&
                                    "border-rose-300 focus-visible:ring-rose-200"
                            )}
                        />
                        {errors.duration ? (
                            <p className="text-xs text-rose-600">{errors.duration}</p>
                        ) : (
                            <p className="text-xs text-gray-500">
                                How long this package lasts, in months (for example 1
                                for monthly, 3 for quarterly, 12 for annual).
                            </p>
                        )}
                    </div>

                    {/* Sort order — lower numbers appear first in lists */}
                    <div className="space-y-2">
                        <Label htmlFor="package-sort-order">Sort order</Label>
                        <Input
                            id="package-sort-order"
                            inputMode="numeric"
                            placeholder="0"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            disabled={submitting}
                            aria-invalid={Boolean(errors.sortOrder)}
                            className={cn(
                                "tabular-nums",
                                errors.sortOrder &&
                                    "border-rose-300 focus-visible:ring-rose-200"
                            )}
                        />
                        {errors.sortOrder ? (
                            <p className="text-xs text-rose-600">
                                {errors.sortOrder}
                            </p>
                        ) : (
                            <p className="text-xs text-gray-500">
                                Lower numbers appear first. Defaults to 0.
                            </p>
                        )}
                    </div>

                    {/* Pricing — per-currency detail. Fields are platform-aware:
                        base price + the selected platform's add-on prices. */}
                    <div className="space-y-3">
                        <Label>Pricing</Label>
                        {errors.price && (
                            <p className="text-xs text-rose-600">{errors.price}</p>
                        )}
                        <div className="space-y-4">
                            {DETAIL_CURRENCIES.map((ccy) => {
                                const syncable =
                                    SYNCABLE_CURRENCIES.includes(ccy);
                                return (
                                    <div
                                        key={ccy}
                                        className="rounded-lg border border-gray-200 p-4 space-y-3"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {ccy}
                                            </span>
                                            {syncable ? (
                                                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600 border border-blue-200">
                                                    Stripe
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                                                    Display only
                                                </span>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {detailFieldsFor(platform).map(
                                                (field) => {
                                                    const fieldError =
                                                        errors[
                                                            `detail_${ccy}_${field.key}`
                                                        ];
                                                    return (
                                                        <div
                                                            key={field.key}
                                                            className="space-y-1"
                                                        >
                                                            <Label
                                                                htmlFor={`detail-${ccy}-${field.key}`}
                                                                className="text-xs text-gray-600"
                                                            >
                                                                {field.label}
                                                                {field.suffix
                                                                    ? ` (${field.suffix})`
                                                                    : ""}
                                                            </Label>
                                                            <Input
                                                                id={`detail-${ccy}-${field.key}`}
                                                                inputMode="decimal"
                                                                placeholder="0"
                                                                value={
                                                                    detailForm[
                                                                        ccy
                                                                    ][
                                                                        field.key
                                                                    ] ?? ""
                                                                }
                                                                onChange={(e) =>
                                                                    updateDetailField(
                                                                        ccy,
                                                                        field.key,
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                disabled={
                                                                    submitting
                                                                }
                                                                aria-label={`${field.label} (${ccy})`}
                                                                aria-invalid={Boolean(
                                                                    fieldError
                                                                )}
                                                                className={cn(
                                                                    "tabular-nums",
                                                                    fieldError &&
                                                                        "border-rose-300 focus-visible:ring-rose-200"
                                                                )}
                                                            />
                                                            {fieldError && (
                                                                <p className="text-[11px] text-rose-600">
                                                                    {fieldError}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-gray-500">
                            Set per-currency pricing. Add-on price fields depend on
                            the selected platform. SGD and MYR sync to Stripe (one
                            Price each); IDR is stored for display only. Blank fields
                            are saved as 0.
                        </p>
                    </div>

                    {/* Features */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label>Features</Label>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addFeatureRow}
                                disabled={submitting}
                            >
                                <PlusIcon className="w-3.5 h-3.5 mr-1" />
                                Add feature
                            </Button>
                        </div>
                        {errors.features && (
                            <p className="text-xs text-rose-600">
                                {errors.features}
                            </p>
                        )}
                        <div className="space-y-2">
                            {featureRows.length === 0 ? (
                                <p className="text-xs text-gray-400">
                                    No features yet. Add a feature to define what
                                    this package includes.
                                </p>
                            ) : (
                                featureRows.map((row, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-2"
                                    >
                                        <Input
                                            placeholder="feature_key"
                                            value={row.key}
                                            onChange={(e) =>
                                                updateFeatureRow(i, {
                                                    key: e.target.value,
                                                })
                                            }
                                            disabled={submitting}
                                            aria-label={`Feature ${i + 1} key`}
                                            className="flex-1 font-mono text-xs"
                                        />
                                        <Input
                                            placeholder="value"
                                            value={row.value}
                                            onChange={(e) =>
                                                updateFeatureRow(i, {
                                                    value: e.target.value,
                                                })
                                            }
                                            disabled={submitting}
                                            aria-label={`Feature ${i + 1} value`}
                                            className="flex-1 text-xs"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeFeatureRow(i)}
                                            disabled={submitting}
                                            aria-label={`Remove feature ${i + 1}`}
                                            className="flex-shrink-0 text-gray-400 hover:text-rose-600"
                                        >
                                            <Trash2Icon className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                        <p className="text-xs text-gray-400">
                            Values like <span className="font-mono">true</span>,{" "}
                            <span className="font-mono">false</span> or a number
                            are stored as their natural type.
                        </p>
                    </div>

                    {/* Active toggle */}
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                        <div className="space-y-0.5 pr-4">
                            <Label className="text-sm font-medium text-gray-900">
                                Active
                            </Label>
                            <p className="text-xs text-gray-500">
                                Active packages are offered on their platform.
                                Inactive packages are archived.
                            </p>
                        </div>
                        <Switch
                            checked={isActive}
                            onCheckedChange={setIsActive}
                            disabled={submitting}
                            aria-label="Active"
                        />
                    </div>

                    {/* Shadow package — hidden from the public catalog (#3004) */}
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                        <div className="space-y-0.5 pr-4">
                            <Label className="text-sm font-medium text-gray-900">
                                Hidden from catalog
                            </Label>
                            <p className="text-xs text-gray-500">
                                Shadow packages are saved but never shown in the
                                public catalog — for custom/negotiated plans or
                                ones prepared before launch.
                            </p>
                        </div>
                        <Switch
                            checked={isShadowPackage}
                            onCheckedChange={setIsShadowPackage}
                            disabled={submitting}
                            aria-label="Hidden from catalog"
                        />
                    </div>

                    {/* Stripe sync — edit mode only (needs a saved package id) */}
                    {isEdit && pkg && (
                        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="space-y-0.5">
                                    <div className="flex items-center gap-2">
                                        <CreditCardIcon className="w-4 h-4 text-blue-600" />
                                        <Label className="text-sm font-medium text-gray-900">
                                            Stripe
                                        </Label>
                                        {stripeSynced && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
                                                <CheckCircle2Icon className="w-3 h-3" />
                                                Synced
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Saves this package, then creates a Stripe
                                        Product and per-currency Prices from it.
                                    </p>
                                    {stripeSynced && stripeSyncedAt && (
                                        <p className="text-xs text-gray-400">
                                            Last synced{" "}
                                            {parseFormat(
                                                stripeSyncedAt,
                                                "D MMM YYYY, HH:mm"
                                            )}
                                        </p>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSync}
                                    disabled={syncing || submitting}
                                    className="flex-shrink-0"
                                >
                                    {syncing ? (
                                        <>
                                            <Loader2Icon className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                            Syncing…
                                        </>
                                    ) : (
                                        <>
                                            <CreditCardIcon className="w-3.5 h-3.5 mr-1.5" />
                                            {stripeSynced
                                                ? "Re-sync to Stripe"
                                                : "Sync to Stripe"}
                                        </>
                                    )}
                                </Button>
                            </div>

                            {syncError && (
                                <div className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2">
                                    <AlertCircleIcon className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-xs text-rose-700">
                                        {syncError}
                                    </p>
                                </div>
                            )}

                            {/* Read-only product + price id chips */}
                            {(stripeProductIds &&
                                Object.keys(stripeProductIds).length > 0) ||
                            (stripePriceIds &&
                                Object.keys(stripePriceIds).length > 0) ||
                            stripePkg?.stripeProductId ? (
                                <div className="space-y-3 pt-1">
                                    {stripeProductIds &&
                                    Object.keys(stripeProductIds).length > 0 ? (
                                        <StripeIdGroup
                                            label="Product IDs"
                                            entries={stripeProductIds}
                                        />
                                    ) : stripePkg?.stripeProductId ? (
                                        <StripeIdGroup
                                            label="Product ID"
                                            entries={{
                                                Stripe: stripePkg.stripeProductId,
                                            }}
                                        />
                                    ) : null}
                                    {stripePriceIds &&
                                        Object.keys(stripePriceIds).length >
                                            0 && (
                                            <StripeIdGroup
                                                label="Price IDs"
                                                entries={stripePriceIds}
                                            />
                                        )}
                                </div>
                            ) : (
                                !syncError && (
                                    <p className="text-xs text-gray-400">
                                        Not synced yet. Sync to generate Stripe
                                        Product and Price IDs.
                                    </p>
                                )
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader2Icon className="w-4 h-4 mr-1.5 animate-spin" />
                                {isEdit ? "Saving…" : "Creating…"}
                            </>
                        ) : isEdit ? (
                            "Save changes"
                        ) : (
                            "Create package"
                        )}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

// Read-only labelled group of Stripe id chips (per-region products /
// per-currency prices). Keys are the region/currency, values the Stripe id.
function StripeIdGroup({
    label,
    entries,
}: {
    label: string;
    entries: Record<string, string>;
}) {
    return (
        <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                {label}
            </p>
            <div className="flex flex-wrap gap-1.5">
                {Object.entries(entries).map(([key, value]) => (
                    <span
                        key={`${key}-${value}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 border border-gray-200 px-2.5 py-0.5 text-xs"
                    >
                        <span className="font-medium text-gray-500">
                            {humanizeKey(key)}
                        </span>
                        <span className="font-mono text-gray-700">{value}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
